import { DateRange } from "@angular/material/datepicker";

export class ClearViewUtils {
    public static getSharedService<T extends new(...args: any[]) => any>(service: T, serviceName: string) {
        if (!window['ClearViewSharedServices'])
            window['ClearViewSharedServices'] = {};
        if (!window['ClearViewSharedServices'][serviceName])
            window['ClearViewSharedServices'][serviceName] = new service();

        return window['ClearViewSharedServices'][serviceName];
    }

    /**
     * Promise wrapping setTimeout()
     */
    public static timeout(milliseconds: number) {
        return new Promise(resolve => window.setTimeout(resolve, milliseconds));
    }

    public static joinWithMax(arr: string[], max: number, separator: string = ',', totalValues: number = arr.length) {
        let displayString = '';
        let numberJoined = 0;
        while (displayString.length < max && numberJoined < arr.length) {
            const item = arr[numberJoined];
            if (Math.min(item.length, max) + displayString.length > max) // this name will push us over, just stop here
                break;

            displayString += item.substr(0, 15);
            numberJoined++;

            if (displayString.length < max && numberJoined < arr.length)
                displayString += separator;
        }

        if (arr.length > numberJoined)
            return `${displayString} ... (${totalValues - numberJoined} More)`;
        else
            return displayString;
    }

    public static joinStringsWithMax(arr: string[] | string, max: number, separator: string = ',', totalValues: number = arr.length) {
        if(typeof arr === 'string') return arr;
        let numberJoined = 0;
        const displayString = arr.reduce((acc, item) => {
            const newLength = acc.length + item.length + separator.length;
            if (newLength <= max) {
                numberJoined++;
                return acc + (acc.length > 0 ? separator + ' ' : '') + item;
            }
            return acc;
        }, '');

        if (arr.length > numberJoined)
            return `${displayString} ...`;
        else
            return displayString;
    }

    /**
     * Capitalizes the first letter of every word in a string and replaces spaces with underscores.
     * @param str will not function unless string has spaces between words
     * @returns string in Upper_Snake_Case
     */
    public static wordsToUpperSnakeCase(str: string) {
        // Replace underscores and spaces with a single space, trim, and split into an array of individual words.
        const words = str.replace(/[_\s]+/g, ' ').trim().split(' ');
        // Capitalize each word and join with underscores.
        return words
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join('_');
    }

    /**
     * Removes special characters from a string NOTE: _ is not removed
     * @param str string to remove special characters from
     * @returns string with special characters removed
     */
    public static removeSpecialCharacters(str: string) {
        let cleanedStr = str?.replace(/[^\w\s]/gi, '');
        return cleanedStr?.replace(/\s+/g, " ");
    }

    /**
     * Many components uses Display names as the key for localization which can be messy. This function will standardize the key ensuring whitespace is removed
     * special characters are removed, and the first letter of every word is capitalized. Along with converting inner spaces to underscores.
     * this is the standard for keys in the language files IE Good_Style_Key_Example
     *
     * Ensure when creating localization keys in the language files you follow this paradigm.
     */
    public static standardizeLocalizationKey(key: string) {
        return this.wordsToUpperSnakeCase(
            this.removeSpecialCharacters(key?.trim().toLowerCase())
        )?.replaceAll(" ", "_");
    }

}

/* These date range functions were copied over from the SOL date-range-wrapper component for use in mapping dates to date range presets in the global filter and widget datepickers.
 * These functions can be removed if at any point the SOL component is updated to handle this on its own. Such as if the SOL component begins passing more details back to our components
 * that tells us whether a preset was selected and, if so, which one.
 */
export class DateRangeUtils {
    public static calculateRelativeDateRange(unit: 'day' | 'week' | 'month' | 'year' | undefined, unitValue: number, includeToday: boolean = false): DateRange<Date> {
        let start: Date, end: Date;
        switch (unit) {
            case 'week':
                ({ start, end } = this.calculateRelativeDateRangeByWeek(unitValue));
                break;
            case 'month':
                ({ start, end } = this.calculateRelativeDateRangeByMonth(unitValue));
                break;
            case 'year':
                ({ start, end } = this.calculateRelativeDateRangeByYear(unitValue));
                break;
            default:
                ({ start, end } = this.calculateRelativeDateRangeByDay(unitValue, includeToday));
                break;
        }

        ({ start, end } = this.setDefaultDateRangeTime(start, end));
        return new DateRange(start, end);
    }

    public static calculateRelativeDateRangeByDay(unitValue: number, includeToday: boolean): { start: Date; end: Date } {
        const { start, end } = this.calculateTodayStartAndEnd();
        const now = new Date(start);
        if (unitValue < 0) {
            start.setDate(now.getDate() + unitValue + (includeToday ? 1 : 0));
            end.setDate(now.getDate() - (includeToday ? 0 : 1));
        } else if (unitValue > 0) {
            start.setDate(now.getDate() + (includeToday ? 0 : 1));
            end.setDate(now.getDate() + unitValue + (includeToday ? -1 : 0));
        }
        return { start, end };
    }

    public static calculateRelativeDateRangeByWeek(unitValue: number): { start: Date; end: Date } {
        const startingDay = 0;
        const { start, end } = this.calculateTodayStartAndEnd();
        start.setDate(start.getDate() - ((start.getDay() - startingDay + 7) % 7)); // set the date to  the current start date of the week
        if (unitValue < 0) {
            end.setFullYear(start.getFullYear(), start.getMonth(), start.getDate() - 1); //set the end week to current week - 1 day
            start.setDate(start.getDate() + unitValue * 7); //set the start week to (unitValue * 7) days
        } else {
            start.setDate(start.getDate() + (unitValue !== 0 ? 7 : 0)); // Move to the start of the next week
            end.setFullYear(start.getFullYear(), start.getMonth(), start.getDate() + (unitValue !== 0 ? unitValue * 7 - 1 : 6));
        }
        return { start, end };
    }

    public static calculateRelativeDateRangeByMonth(unitValue: number): { start: Date; end: Date } {
        const { start, end } = this.calculateTodayStartAndEnd();
        if (unitValue < 0) {
            end.setFullYear(start.getFullYear(), start.getMonth(), 0);
            start.setFullYear(start.getFullYear(), start.getMonth() + unitValue, 1);
        } else {
            start.setFullYear(start.getFullYear(), start.getMonth() + (unitValue !== 0 ? 1 : 0), 1); // Move to the start of the next month
            end.setFullYear(start.getFullYear(), start.getMonth() + (unitValue !== 0 ? unitValue : 1), 0);
        }
        return { start, end };
    }

    public static calculateRelativeDateRangeByYear(unitValue: number): { start: Date; end: Date } {
        const { start, end } = this.calculateTodayStartAndEnd();
        if (unitValue < 0) {
            end.setFullYear(start.getFullYear(), 0, 0);
            start.setFullYear(start.getFullYear() + unitValue, 0, 1);
        } else {
            start.setFullYear(start.getFullYear() + (unitValue !== 0 ? 1 : 0), 0, 1); // Move to the start of the next year
            end.setFullYear(start.getFullYear() + (unitValue !== 0 ? unitValue : 1), 0, 0);
        }
        return { start, end };
    }

    public static getCurrentMonthRange() {
        const { start, end } = this.calculateTodayStartAndEnd();
        start.setDate(1);
        return { start, end };
    }

    public static calculateTodayStartAndEnd(): { start: Date; end: Date } {
        return this.setDefaultDateRangeTime(new Date(), new Date());
    }

    public static setDefaultDateRangeTime(start: Date, end: Date): { start: Date; end: Date } {
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        return { start, end };
    }
}